import {
  EstadoAnteriorBsc,
  EstadoComparativoBsc,
  EstadoEntregaBsc,
  EstadoMesBsc,
  EstatusPlaneacionBsc,
  EstatusTrimestreBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";

/**
 * Cómo se lee en pantalla cada estatus del BSC (FOR-011), con qué clase y
 * con qué icono. Mismo patrón que ESTATUS_SIA
 * (shared/model/iso42001/solicitud-ia.model.ts).
 *
 * Los valores son los de docs/BSC.md §4 y los Javadoc de los beans de
 * CosCore (BscEjercicioBean.estatus, BscEjercicioTrimestreBean.estatus y los
 * semáforos). Las clases se pintan en bsc.shared.css (llegó con T1, F3.5)
 * sobre .table-page__status-badge, con los tokens --bsc-* para los tres
 * temas.
 */
export interface EstadoBsc<T extends string> {
  valor: T;
  texto: string;
  clase: string;
  icono: string;
}

/** La planeación del año de un área (bsc_ejercicio.estatus). */
export const ESTATUS_PLANEACION: EstadoBsc<EstatusPlaneacionBsc>[] = [
  { valor: "BORRADOR", texto: "Borrador", clase: "bsc-es-borrador", icono: "edit_note" },
  { valor: "ENVIADA", texto: "Enviada a autorización", clase: "bsc-es-enviada", icono: "send" },
  { valor: "REGRESADA", texto: "Regresada", clase: "bsc-es-regresada", icono: "undo" },
  { valor: "AUTORIZADA", texto: "Autorizada", clase: "bsc-es-autorizada", icono: "verified" },
];

/**
 * El flujo de cada trimestre (bsc_ejercicio_trimestre.estatus).
 *
 * "Sin enviar" no es un estatus: es la bandera derivada `sinEnviar`
 * (PENDIENTE, EN_CAPTURA o REGRESADO con la fecha límite vencida) y se pinta
 * aparte.
 */
export const ESTATUS_TRIMESTRE: EstadoBsc<EstatusTrimestreBsc>[] = [
  { valor: "PENDIENTE", texto: "Pendiente", clase: "bsc-es-pendiente", icono: "schedule" },
  { valor: "EN_CAPTURA", texto: "En captura", clase: "bsc-es-captura", icono: "edit_calendar" },
  { valor: "ENVIADO", texto: "Enviado a revisión", clase: "bsc-es-enviado", icono: "send" },
  { valor: "REGRESADO", texto: "Regresado", clase: "bsc-es-regresado", icono: "undo" },
  { valor: "AUTORIZADO", texto: "Autorizado", clase: "bsc-es-autorizado", icono: "verified" },
];

/**
 * Semáforo de KPI, área o empresa (docs/BSC.md §3): ratio = real acumulado /
 * programado acumulado contra los umbrales del ciclo. INICIA_Qn = el KPI aún
 * no aplica en ese trimestre; no hay INICIA_Q1 porque todo KPI aplica desde
 * Q1 o después.
 */
export const SEMAFORO: EstadoBsc<SemaforoBsc>[] = [
  { valor: "VERDE", texto: "En meta", clase: "bsc-sem-verde", icono: "check_circle" },
  { valor: "AMARILLO", texto: "En riesgo", clase: "bsc-sem-amarillo", icono: "error_outline" },
  { valor: "ROJO", texto: "Fuera de meta", clase: "bsc-sem-rojo", icono: "cancel" },
  { valor: "SIN_PROGRAMA", texto: "Sin programa", clase: "bsc-sem-sin-programa", icono: "remove_circle_outline" },
  { valor: "INICIA_Q2", texto: "Inicia en Q2", clase: "bsc-sem-inicia", icono: "hourglass_empty" },
  { valor: "INICIA_Q3", texto: "Inicia en Q3", clase: "bsc-sem-inicia", icono: "hourglass_empty" },
  { valor: "INICIA_Q4", texto: "Inicia en Q4", clase: "bsc-sem-inicia", icono: "hourglass_empty" },
];

/**
 * La entrega del trimestre del corte de un área en A1/A2 (estadoEntrega,
 * derivado en BscConsultaBuss, F4). PENDIENTE es "falta enviar y aún no
 * vence" (o no hay fecha límite); VENCIDA, la fecha límite ya pasó; SIN_DATOS,
 * un trimestre de un histórico cargado hasta un Q anterior, que no se espera.
 */
export const ENTREGA: EstadoBsc<EstadoEntregaBsc>[] = [
  { valor: "AUTORIZADA", texto: "Autorizada", clase: "bsc-en-autorizada", icono: "verified" },
  { valor: "ENVIADA", texto: "Enviada a revisión", clase: "bsc-en-enviada", icono: "send" },
  { valor: "PENDIENTE", texto: "Pendiente de envío", clase: "bsc-en-pendiente", icono: "schedule" },
  { valor: "VENCIDA", texto: "Vencida", clase: "bsc-en-vencida", icono: "event_busy" },
  { valor: "SIN_DATOS", texto: "Sin datos", clase: "bsc-en-sin-datos", icono: "remove_circle_outline" },
  { valor: "SIN_PLANEACION", texto: "Sin planeación", clase: "bsc-en-sin-planeacion", icono: "remove_circle_outline" },
];

/**
 * El estado de un mes en la ficha del KPI (BscKpiMes.estado, derivado en
 * BscConsultaBuss, F4.1; se pinta desde A3, F4.3). BLOQUEADO es "el
 * trimestre ya se envió o se autorizó": el mes puede traer su captura o no.
 */
export const ESTADO_MES: EstadoBsc<EstadoMesBsc>[] = [
  { valor: "BLOQUEADO", texto: "Cerrado", clase: "bsc-mes-cerrado", icono: "lock" },
  { valor: "CAPTURADO", texto: "Capturado", clase: "bsc-mes-capturado", icono: "check_circle" },
  { valor: "NO_APLICA", texto: "No aplica", clase: "bsc-mes-no-aplica", icono: "remove_circle_outline" },
  { valor: "VENCIDO", texto: "Vencido", clase: "bsc-mes-vencido", icono: "event_busy" },
  { valor: "PENDIENTE", texto: "Pendiente", clase: "bsc-mes-pendiente", icono: "schedule" },
];

/**
 * Un KPI maestro en el comparativo anual (T2, F7.1; BscComparativoFilaBean.estado).
 * El texto de NUEVO y RETIRADO lleva el año B en pantalla ("Nuevo en 2026").
 */
export const ESTADO_COMPARATIVO: EstadoBsc<EstadoComparativoBsc>[] = [
  { valor: "CONSERVADO", texto: "Conservado", clase: "bsc-comp-conservado", icono: "check" },
  { valor: "META_MODIFICADA", texto: "Meta modificada", clase: "bsc-comp-meta", icono: "edit" },
  { valor: "NUEVO", texto: "Nuevo", clase: "bsc-comp-nuevo", icono: "add_circle_outline" },
  { valor: "RETIRADO", texto: "Retirado", clase: "bsc-comp-retirado", icono: "remove_circle_outline" },
];

/**
 * F7.3: por qué A2 o A3 no tienen con qué comparar contra el año anterior
 * (estadoAnterior del servidor), en una frase sin punto final. `q` es el
 * trimestre comparado y `datosHasta` el último que se cargó de un histórico
 * (SIN_DATOS). NO_APLICA es de A3 y la ficha lo afina con el KPI anterior.
 */
export function textoSinAnterior(
  estado: EstadoAnteriorBsc | null | undefined,
  anterior: number,
  q: number,
  datosHasta?: number | null,
): string {
  switch (estado) {
    case "SIN_LECTURA":
      return `No se pudo consultar ${anterior}; vuelve a intentarlo en un momento`;
    case "SIN_CICLO":
      return `${anterior} no está dado de alta en el BSC`;
    case "SIN_PLANEACION":
      return `El área no tuvo planeación en ${anterior}`;
    case "NUEVO":
      return `El KPI no estaba en la planeación ${anterior} del área: es nuevo este año`;
    case "NO_AUTORIZADA":
      return `La planeación ${anterior} del área no se autorizó: no tiene avances que comparar`;
    case "SIN_DATOS":
      return datosHasta != null
        ? `El FOR-011 de ${anterior} se cargó hasta Q${datosHasta}: no hay datos al Q${q}`
        : `El FOR-011 de ${anterior} no tiene datos al Q${q}`;
    case "NO_APLICA":
      return `En ${anterior} no aplicaba al Q${q}`;
    default:
      return `Sin alcance de ${anterior} al Q${q} para comparar`;
  }
}

/**
 * Cómo se lee cada acción de la bitácora (bsc_historial.accion, catálogo de
 * docs/BSC.md §2). Lo que no esté aquí se enseña tal cual.
 */
export const ACCION_HISTORIAL: Record<string, string> = {
  ALTA_KPI: "Alta del KPI",
  BAJA_KPI: "Baja del KPI",
  CAMBIO_META: "Cambio de meta",
  CAMBIO_PESOS: "Cambio de pesos",
  CAPTURA_MES: "Captura del mes",
  CAPTURA_REAL: "Captura del avance",
  ENVIO_PLANEACION: "Planeación enviada a autorización",
  AUTORIZA_PLANEACION: "Planeación autorizada",
  REGRESA_PLANEACION: "Planeación regresada",
  CIERRE_Q: "Trimestre enviado a revisión",
  AUTORIZA_Q: "Trimestre autorizado",
  REGRESA_Q: "Trimestre regresado",
  REAPERTURA_Q: "Trimestre reabierto",
  IMPORTACION: "Carga del FOR-011",
  FUSION_KPI: "Fusión de KPI",
  PARAMETROS: "Cambio de parámetros",
};
